import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer, Alert, CircularProgress, Chip,
  Divider, IconButton, Tooltip,
} from '@mui/material';
import ArrowBackIcon   from '@mui/icons-material/ArrowBack';
import SaveIcon        from '@mui/icons-material/Save';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import PrintIcon       from '@mui/icons-material/Print';
import ScienceIcon     from '@mui/icons-material/Science';
import { useNavigate } from 'react-router-dom';
import { substrateVsService } from '../services/api';
import { SubstrateVsDailyReport } from '../types';

// ── constants ─────────────────────────────────────────────────────────────────
const REPLICATES = ['R1', 'R2', 'R3'] as const;
const VS_LOW  = 70;
const VS_HIGH = 90;

type RowState = { dishWeightG: string; driedSamplePlusDishG: string; residuePlusDishG: string };
type FormRow  = Record<typeof REPLICATES[number], RowState>;

const emptyRow  = (): RowState => ({ dishWeightG: '', driedSamplePlusDishG: '', residuePlusDishG: '' });
const emptyForm = (): FormRow  => ({ R1: emptyRow(), R2: emptyRow(), R3: emptyRow() });

// ── helpers ───────────────────────────────────────────────────────────────────
const toN  = (s: string) => { const n = parseFloat(s); return isNaN(n) ? null : n; };
const fmt4 = (v: number | null) => v == null ? '—' : v.toFixed(4);

const calcPctVs = (
  dish: number | null,
  driedSamplePlusDish: number | null,
  residuePlusDish: number | null,
) => {
  if (dish == null || driedSamplePlusDish == null || residuePlusDish == null) return null;
  const denominator = driedSamplePlusDish - dish;
  if (denominator === 0) return null;
  return ((driedSamplePlusDish - residuePlusDish) / denominator) * 100;
};

const calcRemark = (avg: number | null) => {
  if (avg == null) return null;
  if (avg < VS_LOW)  return 'Below range';
  if (avg > VS_HIGH) return 'Above range';
  return 'Stable';
};

const remarkColor = (r: string | null) =>
  r === 'Stable' ? 'success' : r === 'Above range' ? 'warning' : r === 'Below range' ? 'error' : 'default';

// ── component ─────────────────────────────────────────────────────────────────
const SubstrateVSForm: React.FC = () => {
  const today    = new Date().toISOString().split('T')[0];
  const navigate = useNavigate();

  const [date,          setDate]          = useState(today);
  const [substrateName, setSubstrateName] = useState('POLOMOLOK');
  const [rows,          setRows]          = useState<FormRow>(emptyForm());
  const [saving,        setSaving]        = useState(false);
  const [loadingDates,  setLoadingDates]  = useState(false);
  const [dates,         setDates]         = useState<string[]>([]);
  const [savedReport,   setSavedReport]   = useState<SubstrateVsDailyReport | null>(null);
  const [error,         setError]         = useState<string | null>(null);
  const [success,       setSuccess]       = useState<string | null>(null);

  // ── computed live preview ────────────────────────────────────────────────
  const computed = REPLICATES.map((rep) => {
    const r                  = rows[rep];
    const dish               = toN(r.dishWeightG);
    const driedSamplePlusDish = toN(r.driedSamplePlusDishG);
    const residuePlusDish    = toN(r.residuePlusDishG);
    const pctVs              = calcPctVs(dish, driedSamplePlusDish, residuePlusDish);
    return { rep, dish, driedSamplePlusDish, residuePlusDish, pctVs };
  });

  const validPcts = computed.map((c) => c.pctVs).filter((v): v is number => v != null);
  const avgPctVs  = validPcts.length ? validPcts.reduce((a, b) => a + b, 0) / validPcts.length : null;
  const remark    = calcRemark(avgPctVs);

  // ── fetch available dates ────────────────────────────────────────────────
  const fetchDates = useCallback(async () => {
    setLoadingDates(true);
    try {
      const d = await substrateVsService.getAvailableDates();
      setDates(d);
    } catch { setDates([]); }
    finally { setLoadingDates(false); }
  }, []);

  useEffect(() => { fetchDates(); }, [fetchDates]);

  // ── load existing data when date changes ─────────────────────────────────
  const loadDate = useCallback(async (d: string) => {
    if (!d) return;
    try {
      const report: SubstrateVsDailyReport = await substrateVsService.getDailyReport(d);
      const sub = report.substrates.find((s) => s.substrateName === substrateName) ?? report.substrates[0];
      if (sub) {
        const newRows = emptyForm();
        for (const rep of REPLICATES) {
          const rr = sub.replicates.find((r) => r.replicate === rep);
          if (rr) {
            newRows[rep] = {
              dishWeightG:          rr.dishWeightG              != null ? String(rr.dishWeightG)          : '',
              driedSamplePlusDishG: rr.driedSamplePlusDishG     != null ? String(rr.driedSamplePlusDishG) : '',
              residuePlusDishG:     rr.residuePlusDishG         != null ? String(rr.residuePlusDishG)     : '',
            };
          }
        }
        setRows(newRows);
        setSavedReport(report);
      } else {
        setRows(emptyForm());
        setSavedReport(null);
      }
    } catch {
      setRows(emptyForm());
      setSavedReport(null);
    }
  }, [substrateName]);

  useEffect(() => { loadDate(date); }, [date, loadDate]);

  const handleCell = (rep: typeof REPLICATES[number], field: keyof RowState, val: string) =>
    setRows((prev) => ({ ...prev, [rep]: { ...prev[rep], [field]: val } }));

  // ── save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const entries = REPLICATES
        .map((rep) => {
          const r = rows[rep];
          return {
            measurementDate:      date,
            substrateName,
            replicate:            rep,
            dishWeightG:          toN(r.dishWeightG),
            driedSamplePlusDishG: toN(r.driedSamplePlusDishG),
            residuePlusDishG:     toN(r.residuePlusDishG),
          };
        })
        .filter((e) => e.dishWeightG != null || e.driedSamplePlusDishG != null || e.residuePlusDishG != null);

      if (!entries.length) { setError('Enter at least one measurement before saving.'); return; }
      await substrateVsService.upsertBatch(entries);
      setSuccess(`Saved ${entries.length} replicate(s) for ${substrateName} on ${date}`);
      await fetchDates();
      await loadDate(date);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!window.confirm(`Delete all ${substrateName} substrate VS entries for ${date}?`)) return;
    try {
      await substrateVsService.deleteByDate(date, substrateName);
      setRows(emptyForm());
      setSavedReport(null);
      setSuccess('Deleted');
      await fetchDates();
    } catch (e: any) {
      setError(e?.message ?? 'Delete failed');
    }
  };

  // ── render ────────────────────────────────────────────────────────────────
  const TH = {
    fontWeight: 700, fontSize: '0.72rem', border: '1px solid #455a64',
    whiteSpace: 'nowrap', bgcolor: '#263238', color: '#fff', px: 1.5, py: 1,
  } as const;
  const TH_PURPLE = { ...TH, bgcolor: '#4a148c', border: '1px solid #6a1b9a' } as const;
  const TD = { border: '1px solid #cfd8dc', fontSize: '0.75rem', px: 1, py: 0.5 } as const;

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* ── Page header ── */}
      <Paper
        className="no-print"
        elevation={0}
        sx={{
          p: 2, mb: 2, borderRadius: 2,
          background: 'linear-gradient(135deg, #4a148c 0%, #6a1b9a 100%)',
          color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => navigate('/lab-report')} sx={{ color: 'rgba(255,255,255,0.7)' }} size="small">
            <ArrowBackIcon />
          </IconButton>
          <ScienceIcon sx={{ fontSize: '2.2rem', opacity: 0.8 }} />
          <Box>
            <Typography variant="overline" sx={{ opacity: 0.7, display: 'block', lineHeight: 1, letterSpacing: 2 }}>
              SUBSTRATE
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Percentage Volatile Solids (%VS)
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              %VS = (Dried+Dish − Residue+Dish) / (Dried+Dish − Dish) × 100 &nbsp;·&nbsp; Target: 70 – 90%
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            label="Substrate Name" size="small"
            value={substrateName}
            onChange={(e) => setSubstrateName(e.target.value.toUpperCase())}
            InputLabelProps={{ shrink: true, style: { color: 'rgba(255,255,255,0.7)' } }}
            inputProps={{ style: { color: '#fff' } }}
            sx={{ minWidth: 150, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' } }}
          />
          <TextField
            label="Measurement Date" type="date" size="small"
            value={date} onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true, style: { color: 'rgba(255,255,255,0.7)' } }}
            inputProps={{ style: { color: '#fff' } }}
            sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' } }}
          />
          <Tooltip title={`Delete all ${substrateName} entries for ${date}`}>
            <span>
              <IconButton
                onClick={handleDelete} size="small"
                disabled={!dates.includes(date)}
                sx={{ color: dates.includes(date) ? '#ef9a9a' : 'rgba(255,255,255,0.2)' }}
              >
                <DeleteSweepIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Print">
            <IconButton onClick={() => window.print()} sx={{ color: '#fff' }} size="small">
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: '#00695c', '&:hover': { bgcolor: '#004d40' }, fontWeight: 700 }}
          >
            Save
          </Button>
        </Box>
      </Paper>

      {/* ── Saved dates chips ── */}
      {(loadingDates || dates.length > 0) && (
        <Box className="no-print" sx={{ mb: 2, display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
          {loadingDates
            ? <CircularProgress size={14} />
            : dates.slice(0, 30).map((d) => (
                <Chip key={d} label={d} size="small" variant={d === date ? 'filled' : 'outlined'}
                  color={d === date ? 'secondary' : 'default'}
                  onClick={() => setDate(d)} sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                />
              ))
          }
        </Box>
      )}

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Entry table ── */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {substrateName} — {date}
        </Typography>
        <TableContainer>
          <Table size="small" sx={{ borderCollapse: 'collapse', minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...TH, minWidth: 70 }}>Replicate #</TableCell>
                <TableCell sx={{ ...TH, minWidth: 145 }}>Dish, Weight (g)</TableCell>
                <TableCell sx={{ ...TH, minWidth: 220 }}>Dried Sample + Dish before ignition (g)</TableCell>
                <TableCell sx={{ ...TH, minWidth: 230 }}>Weight of residue + dish after ignition (g)</TableCell>
                <TableCell sx={{ ...TH, minWidth: 105, bgcolor: '#37474f' }}>%VS</TableCell>
                <TableCell sx={{ ...TH_PURPLE, minWidth: 115 }}>Average %VS</TableCell>
                <TableCell sx={{ ...TH_PURPLE, minWidth: 110 }}>Target Range</TableCell>
                <TableCell sx={{ ...TH_PURPLE, minWidth: 110 }}>Remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {REPLICATES.map((rep, i) => {
                const c = computed[i];
                return (
                  <TableRow key={rep} hover sx={{ bgcolor: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                    <TableCell sx={{ ...TD, fontWeight: 700, color: '#6a1b9a' }}>{rep}</TableCell>
                    <TableCell sx={TD}>
                      <TextField
                        type="number" size="small" variant="standard"
                        value={rows[rep].dishWeightG}
                        onChange={(e) => handleCell(rep, 'dishWeightG', e.target.value)}
                        inputProps={{ step: 0.0001 }} sx={{ width: 115 }}
                        placeholder="e.g. 47.0848"
                      />
                    </TableCell>
                    <TableCell sx={TD}>
                      <TextField
                        type="number" size="small" variant="standard"
                        value={rows[rep].driedSamplePlusDishG}
                        onChange={(e) => handleCell(rep, 'driedSamplePlusDishG', e.target.value)}
                        inputProps={{ step: 0.0001 }} sx={{ width: 130 }}
                        placeholder="e.g. 49.036"
                      />
                    </TableCell>
                    <TableCell sx={TD}>
                      <TextField
                        type="number" size="small" variant="standard"
                        value={rows[rep].residuePlusDishG}
                        onChange={(e) => handleCell(rep, 'residuePlusDishG', e.target.value)}
                        inputProps={{ step: 0.0001 }} sx={{ width: 130 }}
                        placeholder="e.g. 47.7634"
                      />
                    </TableCell>
                    <TableCell sx={{ ...TD, bgcolor: '#f5f5f5', fontWeight: 700,
                      color: c.pctVs == null ? '#9e9e9e' : c.pctVs < VS_LOW ? '#c62828' : c.pctVs > VS_HIGH ? '#e65100' : '#4a148c' }}>
                      {c.pctVs == null ? '—' : c.pctVs.toFixed(8)}
                    </TableCell>
                    {/* Average / Target / Remarks — rowSpan=3 on R1 */}
                    {i === 0 && (
                      <>
                        <TableCell
                          rowSpan={3} align="center"
                          sx={{
                            ...TD, fontWeight: 700, bgcolor: '#f3e5f5', fontSize: '0.85rem',
                            color: avgPctVs == null ? '#9e9e9e' : avgPctVs < VS_LOW ? '#c62828' : avgPctVs > VS_HIGH ? '#e65100' : '#4a148c',
                          }}
                        >
                          {avgPctVs == null ? '—' : avgPctVs.toFixed(2)}
                        </TableCell>
                        <TableCell
                          rowSpan={3} align="center"
                          sx={{ ...TD, bgcolor: '#f3e5f5', fontWeight: 600, color: '#4a148c', fontSize: '0.75rem' }}
                        >
                          70% – 90%
                        </TableCell>
                        <TableCell rowSpan={3} align="center" sx={{ ...TD, bgcolor: '#f3e5f5' }}>
                          {remark ? (
                            <Chip
                              label={remark}
                              color={remarkColor(remark) as any}
                              size="small"
                              sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                            />
                          ) : '—'}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Interpretation ── */}
      <Paper sx={{ p: 2.5, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#263238' }}>
          Interpretation
        </Typography>
        <Divider sx={{ mb: 1.5 }} />
        {[
          { label: 'Stable',       color: '#4a148c', bg: '#f3e5f5', desc: 'Shows healthy biodegradation and good methane yield.' },
          { label: 'Above range',  color: '#e65100', bg: '#fff3e0', desc: 'Excellent methane potential, but may need dilution/mixing to avoid process upset.' },
          { label: 'Below range',  color: '#c62828', bg: '#ffebee', desc: 'Low residual biogas potential.' },
        ].map(({ label, color, bg, desc }) => (
          <Box key={label} sx={{ display: 'flex', gap: 1.5, mb: 1, p: 1, borderRadius: 1, bgcolor: bg }}>
            <Typography fontWeight={700} sx={{ color, minWidth: 100, fontSize: '0.8rem' }}>{label}:</Typography>
            <Typography variant="body2" sx={{ color: '#424242' }}>{desc}</Typography>
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

export default SubstrateVSForm;
